// =============================================================================
// Webinar Session Editor — Admin
// =============================================================================
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { webinarSessionAPI, webinarAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Save, ArrowLeft, Plus, Trash2, Globe, Calendar, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AppLayout } from '@/components/layout/AppLayout';

export default function WebinarSessionEditorPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!sessionId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [webinars, setWebinars] = useState([]);

  const [form, setForm] = useState({
    title: '',
    description: '',
    banner_url: '',
    category: '',
    tags: '',
    schedule: { start_at: '', duration_minutes: 60, timezone: 'Europe/Paris' },
    program: [],
    webinar_id: '',
    max_participants: 100,
    require_approval: true,
    status: 'draft',
  });

  useEffect(() => {
    loadWebinars();
    if (isEdit) loadSession();
  }, [sessionId]);

  const loadWebinars = async () => {
    try {
      const { data } = await webinarAPI.list();
      setWebinars(data || []);
    } catch {}
  };

  const loadSession = async () => {
    setLoading(true);
    try {
      const { data } = await webinarSessionAPI.get(sessionId);
      setForm({
        ...data,
        tags: (data.tags || []).join(', '),
        program: data.program || [],
      });
    } catch {
      toast.error('Session introuvable');
      navigate('/admin/webinar-sessions');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const updateSchedule = (field, value) => {
    setForm(prev => ({ ...prev, schedule: { ...prev.schedule, [field]: value } }));
  };

  const addProgramItem = () => {
    setForm(prev => ({
      ...prev,
      program: [...prev.program, { title: '', description: '', duration_minutes: 30, speaker: '', link_url: '', link_text: '' }],
    }));
  };

  const updateProgramItem = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      program: prev.program.map((p, i) => i === index ? { ...p, [field]: value } : p),
    }));
  };

  const removeProgramItem = (index) => {
    setForm(prev => ({
      ...prev,
      program: prev.program.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Titre requis');
    if (!form.schedule.start_at) return toast.error('Date requise');

    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        schedule: {
          ...form.schedule,
          duration_minutes: parseInt(form.schedule.duration_minutes) || 60,
        },
      };

      if (isEdit) {
        await webinarSessionAPI.update(sessionId, payload);
        toast.success('Session mise à jour');
      } else {
        const { data } = await webinarSessionAPI.create(payload);
        toast.success('Session créée');
        navigate(`/admin/webinar-sessions/${data.id}/edit`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  const publicUrl = form.slug ? `${window.location.origin}/webinar-session/${form.slug}` : null;

  return (
    <AppLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/webinar-sessions')} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold text-foreground">
              {isEdit ? 'Modifier la session' : 'Nouvelle session webinaire'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {isEdit && (
              <Select value={form.status} onValueChange={(v) => updateField('status', v)}>
                <SelectTrigger className="w-36 bg-background border-input text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Brouillon</SelectItem>
                  <SelectItem value="published">Publié</SelectItem>
                  <SelectItem value="closed">Fermé</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-violet-600 gap-2">
              <Save className="w-4 h-4" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>

        {/* Public link */}
        {publicUrl && form.status === 'published' && (
          <div className="mb-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-3">
            <Globe className="w-5 h-5 text-emerald-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-emerald-300 font-medium">Lien public de la session</p>
              <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-400 hover:underline truncate block">{publicUrl}</a>
            </div>
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Copié !'); }} className="text-emerald-400 hover:text-emerald-300">
              Copier
            </Button>
          </div>
        )}

        <div className="space-y-8">
          {/* General Info */}
          <section className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-cyan-500" /> Informations générales
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Titre *</label>
                <Input value={form.title} onChange={(e) => updateField('title', e.target.value)} placeholder="Ex: Formation Docker & Kubernetes" className="bg-background border-input" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Description</label>
                <Textarea value={form.description || ''} onChange={(e) => updateField('description', e.target.value)} placeholder="Décrivez votre session..." rows={4} className="bg-background border-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Catégorie</label>
                  <Input value={form.category || ''} onChange={(e) => updateField('category', e.target.value)} placeholder="DevOps, Cloud..." className="bg-background border-input" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Tags (séparés par des virgules)</label>
                  <Input value={form.tags || ''} onChange={(e) => updateField('tags', e.target.value)} placeholder="docker, kubernetes..." className="bg-background border-input" />
                </div>
              </div>
            </div>
          </section>

          {/* Schedule */}
          <section className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-violet-500" /> Planification
            </h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Date et heure *</label>
                <Input type="datetime-local" value={form.schedule.start_at?.replace('Z', '').slice(0, 16) || ''} onChange={(e) => updateSchedule('start_at', e.target.value)} className="bg-background border-input" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Durée (minutes)</label>
                <Input type="number" value={form.schedule.duration_minutes} onChange={(e) => updateSchedule('duration_minutes', e.target.value)} className="bg-background border-input" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Places max</label>
                <Input type="number" value={form.max_participants} onChange={(e) => updateField('max_participants', parseInt(e.target.value) || 100)} className="bg-background border-input" />
              </div>
            </div>
          </section>

          {/* Linked webinar */}
          <section className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-cyan-500" /> Webinaire Live associé
            </h2>
            <p className="text-xs text-muted-foreground mb-3">Sélectionnez le webinaire live que les candidats rejoindront</p>
            <Select value={form.webinar_id ? String(form.webinar_id) : 'none'} onValueChange={(v) => updateField('webinar_id', v === 'none' ? '' : v)}>
              <SelectTrigger className="bg-background border-input">
                <SelectValue placeholder="Sélectionner un webinaire..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun</SelectItem>
                {webinars.map(w => (
                  <SelectItem key={w.id} value={String(w.id)}>{w.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </section>

          {/* Program */}
          <section className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-foreground">📋 Programme</h2>
              <Button variant="ghost" size="sm" onClick={addProgramItem} className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 gap-1">
                <Plus className="w-3.5 h-3.5" /> Ajouter
              </Button>
            </div>
            {form.program.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Aucun élément dans le programme</p>
            ) : (
              <div className="space-y-3">
                {form.program.map((item, i) => (
                  <div key={i} className="bg-muted/30 rounded-lg p-4 border border-border">
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-xs font-bold shrink-0 mt-1">{i + 1}</span>
                      <div className="flex-1 space-y-2">
                        <Input value={item.title} onChange={(e) => updateProgramItem(i, 'title', e.target.value)} placeholder="Titre du module" className="bg-background border-input text-sm" />
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={item.speaker || ''} onChange={(e) => updateProgramItem(i, 'speaker', e.target.value)} placeholder="Intervenant" className="bg-background border-input text-sm" />
                          <Input type="number" value={item.duration_minutes || ''} onChange={(e) => updateProgramItem(i, 'duration_minutes', parseInt(e.target.value) || 0)} placeholder="Durée (min)" className="bg-background border-input text-sm" />
                        </div>
                        <Textarea value={item.description || ''} onChange={(e) => updateProgramItem(i, 'description', e.target.value)} placeholder="Description..." rows={2} className="bg-background border-input text-sm" />
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={item.link_url || ''} onChange={(e) => updateProgramItem(i, 'link_url', e.target.value)} placeholder="URL du lien (ex: https://...)" className="bg-background border-input text-sm" />
                          <Input value={item.link_text || ''} onChange={(e) => updateProgramItem(i, 'link_text', e.target.value)} placeholder="Texte du lien (ex: Support PDF)" className="bg-background border-input text-sm" />
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeProgramItem(i)} className="text-muted-foreground hover:text-red-500 hover:bg-red-50 shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </AppLayout>
  );
}
